import { FC, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Stack,
  styled,
} from "@mui/material";
import { History, HistoryAnswer, Responses } from "../../types";
import { useHistoryQuestionsContext } from "../HistoryQuestionsContext";
import QuestionCard from "../QuestionCard";

const toResponsesObject = (answers: HistoryAnswer[]) =>
  answers.reduce((acc, item) => {
    acc[item.questionId] = item.value;
    return acc;
  }, {} as Responses);

const HistoryFormControl = styled(FormControl)({
  marginTop: "1rem",
});

const Spacer = styled("div")({
  flexGrow: 1,
});

interface HistoryDialogProps {
  providedHistory?: History;
  addPatientHistory?: (responses: Responses) => void;
  editPatientHistory?: (historyId: number, responses: Responses) => void;
  deletePatientHistory?: (historyId: number) => void;
  handleClose: () => void;
  open: boolean;
  variant: "add" | "edit";
}

const HistoryDialog: FC<HistoryDialogProps> = ({
  providedHistory,
  addPatientHistory,
  editPatientHistory,
  deletePatientHistory,
  handleClose,
  open,
  variant,
}) => {
  const historyQuestions = useHistoryQuestionsContext();
  const [responses, setResponses] = useState<Responses>(
    providedHistory ? toResponsesObject(providedHistory.answers) : {},
  );

  const disabled =
    Object.keys(responses).length !== historyQuestions.length ||
    Object.keys(responses).some((key) => {
      const value = responses[key];
      return typeof value === "number" ? isNaN(value) : false;
    });

  const updateResponses = (
    questionId: string,
    value: string | number | boolean,
  ) => {
    setResponses({ ...responses, [questionId]: value });
  };

  const dialogTitle =
    variant === "add" ? "Add patient history" : "Edit patient history";
  const primaryButtonLabel = variant === "add" ? "Add" : "Update";

  const handleAdd = () => {
    addPatientHistory!(responses);
    handleClose();
  };

  const handleDelete = () => {
    deletePatientHistory!(providedHistory!.historyId);
    handleClose();
  };

  const handleEdit = () => {
    editPatientHistory!(providedHistory!.historyId, responses);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <HistoryFormControl>
          <Stack spacing={2}>
            {historyQuestions.map((question, idx) => (
              <QuestionCard
                key={question.questionId}
                responses={providedHistory ? responses : undefined}
                index={idx + 1}
                question={question}
                updateResponses={updateResponses}
              />
            ))}
          </Stack>
        </HistoryFormControl>
      </DialogContent>
      <DialogActions>
        {variant === "edit" && (
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        )}
        <Spacer />
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={variant === "add" ? handleAdd : handleEdit}
          variant="contained"
          disableElevation
          disabled={disabled}
        >
          {primaryButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoryDialog;
